// Derived, role-specific "whose turn is it" status. Kept React-free and unit-
// tested. The stored company.status / submission.status model and the backend
// compliance rollup are unchanged — this is purely a presentation derivation
// (an extension of awaitingReply.ts to a full three-state model with labels).
import type { SubmissionStatus } from './types.ts';
import { hasPublishedRework } from '@l2u/shared/reviewTurn';

// Re-exported so UI callers keep a single import site for the turn rules.
// The definition lives in @l2u/shared because the API needs it too and
// cannot depend on this React package.
export { hasPublishedRework };

export type ReviewTurn = 'awaiting_admin' | 'awaiting_vendor' | 'compliant' | 'in_progress';

export type ReviewSubmission = {
  status: SubmissionStatus;
  latest_comment_author?: 'admin' | 'vendor' | null;
  /** When the vendor published a rework of a sent-back answer, by replying on
   * the thread. Cleared by the admin's next verdict. See hasPublishedRework. */
  resubmitted_at?: string | null;
  /** The admin's last response — a verdict publish or a standalone comment. A
   * vendor edit nulls it while keeping status 'rejected'. */
  reviewed_at?: string | null;
  /** Set while a previously approved compliance document is in a renewal
   * cycle. Read by isDocumentsTabDocument (@l2u/shared/documentsTab). */
  reverify_requested_at?: string | null;
  /** Who spoke last in the document's current comment round (vendor-visible
   * messages only). Read by the Documents-tab turn rules. */
  current_round_latest_author?: 'admin' | 'vendor' | null;
};

/**
 * The submissions a review verdict can legitimately be drawn from: one per
 * question that HAS a submission, excluding compliance-exempt questions and
 * questions not currently visible to the vendor.
 *
 * Two populations must be dropped, for the same underlying reason — nobody will
 * ever review them, so a 'pending' row would pin reviewTurn at awaiting_admin
 * forever ("Action required" on the admin pill that no reviewing could clear,
 * and "Submitted — under review by Admin" shown to a vendor whose questionnaire
 * was in fact fully approved):
 *   - `exclude_from_compliance` questions: answered but never reviewed.
 *   - questions hidden by their parent's answer or a visibility_rule: the vendor
 *     cannot see or act on them, and their row survives a parent answer flip.
 *
 * `visibleIds` is REQUIRED rather than optional on purpose. The exempt filter was
 * centralised here with a comment asking future callers not to reintroduce the
 * bug; the visibility half of it was then reintroduced anyway, through callers
 * that passed unfiltered question sets. A required parameter makes the type
 * checker enforce what the comment could not.
 */
export function reviewableSubmissions<S extends ReviewSubmission>(
  questions: { id: string; exclude_from_compliance: boolean }[],
  subByQ: ReadonlyMap<string, S>,
  visibleIds: ReadonlySet<string>,
): S[] {
  const result: S[] = [];
  for (const question of questions) {
    if (question.exclude_from_compliance) continue;
    if (!visibleIds.has(question.id)) continue;
    const sub = subByQ.get(question.id);
    if (sub) result.push(sub);
  }
  return result;
}

/**
 * Whether a single submission is currently the VENDOR's turn — i.e. the vendor
 * still owes a response. True when the admin left the most recent comment, or
 * sent the answer back (rejected) and the vendor has not published their rework
 * yet. Approved / not_applicable items are "cleared" and never count (a stale
 * admin comment on an approved item does not reopen it). A plain pending item —
 * vendor submitted, no admin response yet — is awaiting ADMIN, so this returns
 * false: there is nothing for the vendor to action.
 *
 * The hasPublishedRework arm is what clears the vendor's "Action required"
 * banner and badges after they reply, and what moves the company onto the
 * admin's queue. A sent-back answer keeps status 'rejected' through the whole
 * rework (see hasPublishedRework), so without it every vendor-facing signal
 * stayed lit until the admin's next verdict.
 *
 * An admin comment wins over the stamp: if the admin replied after the resubmit
 * then latest_comment_author is 'admin' again and the ball is back with the
 * vendor, regardless of the older stamp.
 */
export function isAwaitingVendor(sub: ReviewSubmission): boolean {
  if (sub.status === 'approved' || sub.status === 'not_applicable') return false;
  if (sub.latest_comment_author === 'admin') return true;
  return sub.status === 'rejected' && !hasPublishedRework(sub);
}

/**
 * Whether a single submission is currently the ADMIN's turn — i.e. the admin
 * still owes a review. True for an uncleared item (not approved / not
 * not_applicable) that is not awaiting the vendor: a freshly-submitted pending
 * item, or one the vendor replied to last. False once the admin has sent it
 * back (rejected) or commented last (that is the vendor's turn), and false for
 * approved / not_applicable items. Per-item complement of isAwaitingVendor over
 * uncleared submissions; consistent with reviewTurn's awaiting_admin branch.
 */
export function isAwaitingAdmin(sub: ReviewSubmission): boolean {
  if (sub.status === 'approved' || sub.status === 'not_applicable') return false;
  return !isAwaitingVendor(sub);
}

/**
 * Classify a set of submissions (one questionnaire, or a whole vendor).
 * Vendor-priority: if the admin has sent ANYTHING back — a rejection or the most
 * recent comment — the vendor owes a response, even if other items still await
 * admin review. Approved / not_applicable items are "cleared" and never drive
 * the turn (a stale admin comment on an approved item does not reopen it).
 */
export function reviewTurn(subs: ReviewSubmission[]): ReviewTurn {
  let awaitingVendor = false;
  let awaitingAdmin = false;
  let hasUncleared = false;
  for (const s of subs) {
    if (s.status === 'approved' || s.status === 'not_applicable') continue;
    hasUncleared = true;
    if (isAwaitingVendor(s)) awaitingVendor = true;
    else awaitingAdmin = true; // pending (incl. vendor replied last)
  }
  if (awaitingVendor) return 'awaiting_vendor';
  if (awaitingAdmin) return 'awaiting_admin';
  return subs.length > 0 && !hasUncleared ? 'compliant' : 'in_progress';
}

// Vendor-facing banner copy. null = render no banner.
export const VENDOR_REVIEW_LABEL: Record<ReviewTurn, string | null> = {
  awaiting_admin: 'Submitted — under review by Admin. No changes can be made whilst under review.',
  awaiting_vendor: 'Action required — please review and update the questions marked below.',
  compliant: 'Compliant — your questionnaire has been approved.',
  in_progress: null,
};

// Admin-facing per-questionnaire pill copy. null = render no pill.
export const ADMIN_REVIEW_LABEL: Record<ReviewTurn, string | null> = {
  awaiting_admin: 'Action required',
  awaiting_vendor: 'Vendor change requested',
  compliant: 'Compliant',
  in_progress: null,
};
